import React, { useState, useEffect } from "react";
import { supabase } from "../supabaseClient";
import { Users, FileText, AlertCircle, MessageSquare, ArrowRight, TrendingUp } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import "../styles/Dashboard.css";

const DashboardHome = () => {
  const [stats, setStats] = useState({ quotes: 0, claims: 0, inquiries: 0, agents: 0 });
  const [recentMessages, setRecentMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  // Data for the Chart
  const chartData = [
    { name: 'Quotes', value: stats.quotes, color: '#3b82f6' },
    { name: 'Claims', value: stats.claims, color: '#ef4444' },
    { name: 'Inquiries', value: stats.inquiries, color: '#a855f7' },
  ];

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const { count: quoteCount } = await supabase.from("quotes").select("*", { count: 'exact', head: true });
      const { count: claimCount } = await supabase.from("claims").select("*", { count: 'exact', head: true });
      const { count: inquiryCount } = await supabase.from("contact_inquiries").select("*", { count: 'exact', head: true });
      const { count: staffCount } = await supabase.from("staff").select("*", { count: 'exact', head: true });
      
      const { data: latestMsgs } = await supabase
        .from("contact_inquiries")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(3);

      setStats({
        quotes: quoteCount || 0,
        claims: claimCount || 0,
        inquiries: inquiryCount || 0,
        agents: staffCount || 0
      });
      setRecentMessages(latestMsgs || []);
    } catch (error) {
      console.error("Dashboard Sync Error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();

    // REALTIME SUBSCRIPTION: Listen for new inquiries
    const inquirySubscription = supabase
      .channel('public:contact_inquiries')
      .on('postgres_changes', { event: '*', table: 'contact_inquiries' }, () => {
        fetchDashboardData(); // Auto-refresh data when database changes
      })
      .subscribe();

    return () => {
      supabase.removeChannel(inquirySubscription);
    };
  }, []);

  return (
    <div className="home-wrapper">
      <header className="home-header">
        <div className="header-info">
          <h1>Welcome, Admin</h1>
          <p>Insurance Activity Overview</p>
        </div>
        <div className="system-status">
          <span className="pulse"></span> System Live
        </div>
      </header>

      {/* Metrics Section */}
      <div className="metrics-grid">
        <div className="metric-box">
          <div className="metric-icon blue"><FileText size={22} /></div>
          <div className="metric-content">
            <h2>{loading ? "..." : stats.quotes}</h2>
            <p>Quotes</p>
          </div>
        </div>
        <div className="metric-box">
          <div className="metric-icon red"><AlertCircle size={22} /></div>
          <div className="metric-content">
            <h2>{loading ? "..." : stats.claims}</h2>
            <p>Claims</p>
          </div>
        </div>
        <div className="metric-box">
          <div className="metric-icon purple"><MessageSquare size={22} /></div>
          <div className="metric-content">
            <h2>{loading ? "..." : stats.inquiries}</h2>
            <p>Inquiries</p>
          </div>
        </div>
        <div className="metric-box">
          <div className="metric-icon green"><Users size={22} /></div>
          <div className="metric-content">
            <h2>{stats.agents}</h2>
            <p>Agents</p>
          </div>
        </div>
      </div>

      <div className="dashboard-lower-grid">
        {/* CHART SECTION */}
        <section className="chart-section">
          <div className="section-header">
            <h3>Activity Distribution</h3>
            <TrendingUp size={18} className="text-slate-400" />
          </div>
          <div className="chart-container" style={{ width: '100%', height: 300 }}>
            <ResponsiveContainer>
              <BarChart data={chartData} margin={{ top: 20, right: 30, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{fill: '#64748b', fontSize: 12}} />
                <YAxis hide />
                <Tooltip 
                  cursor={{fill: '#f8fafc'}}
                  contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }}
                />
                <Bar dataKey="value" radius={[4, 4, 0, 0]} barSize={40}>
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </section>

        {/* Recent Inquiries Feed */}
        <section className="activity-feed">
          <div className="section-header">
            <h3>Recent Inquiries</h3>
            <button className="view-all-link">View All <ArrowRight size={14}/></button>
          </div>
          <div className="feed-container">
            {recentMessages.length > 0 ? recentMessages.map((msg) => (
              <div key={msg.id} className="feed-item">
                <div className="feed-avatar">{msg.full_name.charAt(0)}</div>
                <div className="feed-info">
                  <strong>{msg.full_name}</strong>
                  <p>{msg.message.substring(0, 45)}...</p>
                </div>
                <span className="feed-time">
                  {new Date(msg.created_at).toLocaleDateString()}
                </span>
              </div>
            )) : <p className="empty-msg">No recent activity</p>}
          </div>
        </section>
      </div>
    </div>
  );
};

export default DashboardHome;